/**
 * @module project/components/entry_details_component
 */
import React, { Component, PropTypes } from 'react';
import { v4 } from 'node-uuid';
import partial from 'lodash/partial';
import { scroller } from 'react-scroll';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { LoadingIndicator } from 'common/components/preloader';
import SingleEntryDetails from 'common/components/modal/single_entry_details';
import EntryDetailsRevisions from 'project/components/entry_details/entry_details_revisions';
import EntryDetailsComments from 'project/components/entry_details/entry_details_comments';
import EntryCommentModal from 'project/containers/entry_comment_modal';
import RevisionFeedback from 'project/containers/entries/feedback';
import PinchView from 'common/components/pinch_view';
import Thumbnail from 'common/components/thumbnail';
import { isMobile } from 'common/utils';

/* expected width and height of in-place comment modal */
const COMMENT_MODAL_WIDTH = 400;
const COMMENT_MODAL_HEIGHT = 230;

/**
 * Details component for the project single entry.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class EntryDetailsComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showCommentsModal: false,
      commentsModalPosition: {},
      commentsPosition: {},
      hoveredComment: 0,
      commentReply: null,
    };

    this.addReply = this.addReply.bind(this);
    this.onCommentHover = this.onCommentHover.bind(this);
    this.replyButtonClicked = this.replyButtonClicked.bind(this);
    this.toggleCommentsModal = this.toggleCommentsModal.bind(this);
  }

  /*
   * Called after reply was added
   */
  addReply() {
    this.setState({
      hoveredComment: 0,
      commentReply: null,
    });
  }

  onCommentHover(e, comment) {
    /* ignore hovering for comments other than in-place ones */
    const id = comment && this.isInPlace(comment)? comment.id : 0;

    this.setState({
      hoveredComment: id,
    });
  }

  /*
   * We need to save in state the fact thet we're dealing with
   * a comment reply.
   */
  replyButtonClicked(commentId) {
    this.setState({
      commentReply: commentId,
    });
  }

  toggleCommentsModal(e) {
    const { detailsState, permissions } = this.props;

    if (permissions.canComment && detailsState.zoom === 1) {
      const showCommentsModal = !this.state.showCommentsModal;
      let event;
      let commentsPosition = {};
      let commentsModalPosition = {};

      if (e && e.nativeEvent && e.nativeEvent.srcElement) {
        event = e.nativeEvent;
        commentsPosition = {
          x: ~~((event.offsetX / event.srcElement.width) * 100),
          y: ~~((event.offsetY / event.srcElement.height) * 100),
        };
        /* position modal to the left of clicked point in case it would go offscreen */
        const modalX = (
          event.clientX + COMMENT_MODAL_WIDTH > window.innerWidth?
          event.offsetX - COMMENT_MODAL_WIDTH : event.offsetX
        );
        /* position modal to the top of clicked point in case it would go offscreen */
        const modalY = (
          event.clientY + COMMENT_MODAL_HEIGHT > window.innerHeight?
          event.offsetY - COMMENT_MODAL_HEIGHT : event.offsetY
        );
        commentsModalPosition = { x: modalX, y: modalY };
      }

      this.setState({
        showCommentsModal,
        commentsModalPosition,
        commentsPosition,
      });
    }
  }

  isInPlace(comment) {
    return comment.percent_x || comment.percent_y;
  }

  renderInPlaceComments() {
    const { detailsState, comments, permissions } = this.props;
    const { hoveredComment } = this.state;
    const commentsIndicators = [];
    const imageEl = document.getElementById('revision_image');

    if (permissions.canComment && detailsState.zoom === 1 && imageEl) {
      let pinStyle = null;

      comments.filter(this.isInPlace).forEach((comment, i) => {
        pinStyle = {
          top: `${(comment.percent_y * imageEl.height)/100}px`,
          left: `${(comment.percent_x * imageEl.width)/100}px`
        };

        commentsIndicators.push(
          <span
            key={v4()}
            className={classNames('badge badge-primary badge-md comment-pin',
              { hovered: hoveredComment === comment.id,
                dimmed: hoveredComment && hoveredComment !== comment.id })}
            style={pinStyle}
            onMouseEnter={(e) => { this.onCommentHover(e, comment); }}
            onMouseLeave={(e) => this.onCommentHover(e)}
          >
            { i + 1 }
          </span>
        );
      });
    }

    return commentsIndicators;
  }

  /*
   * Creates element to be rendered in the left drawer.
   *
   * @returns {object} object with css class and element to render
   */
  getLeftDrawer() {
    const { detailsState, entry, revision, comments, user, project,
      onToggleLeftDrawer, onChangeRevision, permissions, scoredRevision } = this.props;
    const { hoveredComment, commentReply } = this.state;
    let element = null;
    let classes = '';

    if (scoredRevision) {
      element = <RevisionFeedback hideThumbnail={true}/>;
    } else if (detailsState.entryRevisionsVisible) {
      element = (
        <EntryDetailsRevisions
          entry={entry}
          currentRevision={detailsState.currentRevision}
          onRevisionChange={onChangeRevision}
          onCloseRevisions={partial(onToggleLeftDrawer, 'revisions')}
        />
      );
      classes = "revisions";
    } else if (detailsState.entryCommentsVisible) {
      element = (
        <EntryDetailsComments
          project={project}
          user={user}
          permissions={permissions}
          entry={entry}
          revision={revision}
          comments={comments}
          hoveredComment={hoveredComment}
          commentReply={commentReply}
          onCommentHover={this.onCommentHover}
          onCloseComments={partial(onToggleLeftDrawer, 'comments')}
          onAddReply={this.addReply}
          onReplyButtonClicked={this.replyButtonClicked}
        />
      );
      classes = "comments";
    }

    return {
      element,
      classes,
    };
  }

  /*
   * Creates element to be rendered as the main content.
   *
   * @returns {object} object with element to render
   */
  getContent() {
    const { permissions, detailsState, revision, entry, onZoomIn, onZoomOut } = this.props;
    const elements = [];
    /* elimination is a form of scoring, so just show the "eliminated" message if user
       can see the score */
    const showEliminatedMsg = entry.eliminated && permissions.canViewScore;

    if (!detailsState.showPreloader && entry.withdrawn && !permissions.canViewWithdrawn) {
      return {
        elements: [<div key="withdrawn" className="message withdrawn"><h1>withdrawn</h1></div>]
      }
    }

    if (!detailsState.showPreloader && detailsState.zoom === 1 && showEliminatedMsg) {
      elements.push(<div key="eliminated" className="flag eliminated"><div>eliminated</div></div>);
    }

    if (detailsState.showPreloader) {
      elements.push(<LoadingIndicator key="loading_indicator" />);
    } else if (entry.entry_type == 'image') {
      elements.push(
        <PinchView
          key="pinch_view"
          debug={false}
          imageSize={{ width: revision.width, height: revision.height }}
          zoom={detailsState.zoom}
          maxScale={detailsState.maxScale}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onToggleFullscreen={this.props.onToggleFullscreen}
          showFullscreen={true}
        >
          {this.renderInPlaceComments()}

          {/* @IMPORTANT: the entry comment modal has to be placed before the img below,
              because its positioning relies on that. */}
          { this.state.showCommentsModal &&
            <EntryCommentModal
              position={this.state.commentsModalPosition}
              commentsPosition={this.state.commentsPosition}
              onToggleModal={this.toggleCommentsModal}
              revision={revision}
              entry={entry}
              canSendInternal={permissions.canSendInternalComment}
              canSendInternalOnly={permissions.canSendInternalCommentOnly}
              isClient={permissions.isClient}
              isCollaborator={permissions.isCollaborator}
              hasCollaborators={permissions.hasCollaborators}
            /> }

          <img
            data-tip
            id="revision_image"
            src={revision.image}
            data-for="add-comment"
            data-event={isMobile()? "click" : undefined}
            onClick={this.toggleCommentsModal}
          />
        </PinchView>
      );
    } else {
      elements.push(
        <Thumbnail className="thumbnail name full" key="name_revision">
          <h1>{revision.name}</h1>
        </Thumbnail>
      )
    }

    if (entry.entry_type == 'image' && permissions.canComment && detailsState.zoom === 1) {
      elements.push(
        <ReactTooltip
          place="right"
          type="dark"
          effect="float"
          key="add-comment"
          id="add-comment"
        >
          <span className="comment-tooltip">
            Click to add a comment
          </span>
        </ReactTooltip>
      );
    }

    return {
      elements,
    };
  }

  render() {
    const leftDrawerData = this.getLeftDrawer();
    const contentData = this.getContent();

    return (
      <SingleEntryDetails
        leftDrawer={leftDrawerData}
        mainContent={contentData}
        detailsState={this.props.detailsState}
      />
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} user - logged in user
 * @property {object} permissions - object with user privileges
 * @property {object} detailsState - object with instance data for rendering purposes
 * @property {object} entry - entry data object
 * @property {object} revision - revision data object
 * @property {array} comments - comments list to render
 * @property {func} onZoomIn - function called when zoom-in button is clicked
 * @property {func} onZoomOut - function called when zoom-out button is clicked
 * @property {func} onChangeRevision - function called when revision is to be changed
 * @property {func} onToggleFullscreen - function called when the fullscreen button is clicked
 * @property {func} onToggleLeftDrawer - function called when we're to toggle the left drawer
 */
EntryDetailsComponent.propTypes = {
  user: PropTypes.object,
  permissions: PropTypes.object,
  detailsState: PropTypes.object,
  entry: PropTypes.object,
  revision: PropTypes.object,
  comments: PropTypes.array,
  onZoomIn: PropTypes.func,
  onZoomOut: PropTypes.func,
  scoredRevision: PropTypes.number,
  onChangeRevision: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  onToggleLeftDrawer: PropTypes.func,
};



// WEBPACK FOOTER //
// ./project/components/entry_details/entry_details_component.jsx